import { config } from '@/app/config';
import { getSupabase } from '@/supabase/client';
import { createId, nowIso } from '@/domain/helpers';
import { getDb, type DayMediaRow } from '@/storage/db';
import {
  extensionForImageMime,
  isSupportedImageFile,
  normalizeImageMime,
} from '@/platform/images';

const DAY_MEDIA_BUCKET = 'day-media';
export const MAX_DAY_MEDIA_BYTES = 8 * 1024 * 1024;

export type DayMediaSourceKind = 'blob' | 'data' | 'public' | 'transform';

export interface DayMediaSrc {
  /** Preferred display URL (public or blob — never a sole transform). */
  url: string;
  /** Optional transform URL; UI may try it first then fall back to `url`. */
  transformUrl: string | null;
  kind: DayMediaSourceKind;
  storagePath: string | null;
  mediaId: string;
}

export interface DayMediaPreview {
  dateKey: string;
  count: number;
  /** @deprecated use sources */
  thumbUrl: string | null;
  /** @deprecated use sources */
  thumbUrls: string[];
  sources: DayMediaSrc[];
}

export async function listDayMedia(spaceId: string, dateKey: string): Promise<DayMediaRow[]> {
  const db = getDb();
  const local = await db.dayMedia.where('[spaceId+dateKey]').equals([spaceId, dateKey]).toArray();

  if (config.mode !== 'production') {
    return local.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  const supabase = getSupabase();
  if (!supabase) return local;

  const folder = `${spaceId}/${dateKey}`;
  const { data: remote, error } = await supabase.storage.from(DAY_MEDIA_BUCKET).list(folder, {
    limit: 50,
  });
  if (error || !remote) {
    return local.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  const byId = new Map(local.map((row) => [row.id, row]));
  for (const file of remote) {
    if (!file.name || file.name.endsWith('/')) continue;
    const id = file.name.replace(/\.[^.]+$/, '');
    const path = `${folder}/${file.name}`;
    const existing = byId.get(id);
    if (existing) {
      byId.set(id, { ...existing, storagePath: path });
      continue;
    }
    byId.set(id, {
      id,
      spaceId,
      dateKey,
      ownerId: '',
      mimeType: file.metadata?.mimetype ?? 'image/jpeg',
      fileName: file.name,
      storagePath: path,
      blob: null,
      createdAt: file.created_at ?? nowIso(),
    });
  }

  return [...byId.values()].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export function resolveDayMediaUrl(row: DayMediaRow): string | null {
  return resolveDayMediaSrc(row)?.url ?? null;
}

/**
 * Stable display source. Public object URL is the reliable primary.
 * Transform URL is optional hint only — Image Transformation is often unavailable
 * and must never be the sole src (it renders as grey broken tiles).
 */
export function resolveDayMediaSrc(row: DayMediaRow): DayMediaSrc | null {
  if (row.blob) {
    return {
      url: URL.createObjectURL(row.blob),
      transformUrl: null,
      kind: 'blob',
      storagePath: row.storagePath,
      mediaId: row.id,
    };
  }
  if (row.storagePath?.startsWith('data:')) {
    return {
      url: row.storagePath,
      transformUrl: null,
      kind: 'data',
      storagePath: null,
      mediaId: row.id,
    };
  }
  if (config.mode !== 'production' || !row.storagePath) return null;
  const publicUrl = publicObjectUrl(row.storagePath);
  if (!publicUrl) return null;
  return {
    url: publicUrl,
    transformUrl: transformObjectUrl(row.storagePath, 160),
    kind: 'public',
    storagePath: row.storagePath,
    mediaId: row.id,
  };
}

/** @deprecated use resolveDayMediaSrc — kept for callers; returns public/blob only. */
export function resolveDayMediaThumbUrl(row: DayMediaRow, _width = 80): string | null {
  return resolveDayMediaSrc(row)?.url ?? null;
}

export async function addDayMedia(input: {
  spaceId: string;
  dateKey: string;
  ownerId: string;
  file: File;
}): Promise<DayMediaRow> {
  const { spaceId, dateKey, ownerId, file } = input;
  if (!isSupportedImageFile(file)) throw new Error('unsupported_image');
  if (file.size > MAX_DAY_MEDIA_BYTES) throw new Error('too_large');

  const id = createId();
  const mime = normalizeImageMime(file);
  const ext = extensionForImageMime(mime, file.name);
  const createdAt = nowIso();
  let storagePath: string | null = null;

  // Keep local blob immediately so UI never goes grey during upload.
  const localRow: DayMediaRow = {
    id,
    spaceId,
    dateKey,
    ownerId,
    mimeType: mime,
    fileName: file.name || `image.${ext}`,
    storagePath: null,
    blob: file,
    createdAt,
  };
  await getDb().dayMedia.put(localRow);

  if (config.mode === 'production') {
    const supabase = getSupabase();
    if (!supabase) throw new Error('no_supabase');
    storagePath = `${spaceId}/${dateKey}/${id}.${ext}`;
    const { error } = await supabase.storage.from(DAY_MEDIA_BUCKET).upload(storagePath, file, {
      upsert: false,
      contentType: mime,
      cacheControl: '3600',
    });
    if (error) throw error;
    const uploaded: DayMediaRow = { ...localRow, storagePath, blob: file };
    await getDb().dayMedia.put(uploaded);
    return uploaded;
  }

  const dataUrl = await readAsDataUrl(file);
  const demoRow: DayMediaRow = { ...localRow, storagePath: dataUrl, blob: file };
  await getDb().dayMedia.put(demoRow);
  return demoRow;
}

export async function removeDayMedia(row: DayMediaRow, viewerId: string): Promise<void> {
  if (row.ownerId && row.ownerId !== viewerId) {
    throw new Error('not_owner');
  }
  if (config.mode === 'production' && row.storagePath && !row.storagePath.startsWith('data:')) {
    const supabase = getSupabase();
    if (supabase) await supabase.storage.from(DAY_MEDIA_BUCKET).remove([row.storagePath]);
  }
  await getDb().dayMedia.delete(row.id);
}

export async function countDayMediaByDates(
  spaceId: string,
  dateKeys: string[],
): Promise<Record<string, number>> {
  const previews = await loadDayMediaPreviews(spaceId, dateKeys);
  const result: Record<string, number> = {};
  for (const key of dateKeys) result[key] = 0;
  for (const [key, preview] of Object.entries(previews)) {
    result[key] = preview.count;
  }
  return result;
}

/** First thumbs + count per date for calendar cells (Dexie + Storage). */
export async function loadDayMediaPreviews(
  spaceId: string,
  dateKeys: string[],
): Promise<Record<string, DayMediaPreview>> {
  const unique = [...new Set(dateKeys)];
  const result: Record<string, DayMediaPreview> = {};
  for (const key of unique) {
    result[key] = { dateKey: key, count: 0, thumbUrl: null, thumbUrls: [], sources: [] };
  }

  const local = await getDb()
    .dayMedia.where('spaceId')
    .equals(spaceId)
    .filter((row) => unique.includes(row.dateKey))
    .toArray();

  const localByDate = new Map<string, DayMediaRow[]>();
  for (const row of local) {
    const list = localByDate.get(row.dateKey) ?? [];
    list.push(row);
    localByDate.set(row.dateKey, list);
  }

  for (const key of unique) {
    const rows = (localByDate.get(key) ?? []).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    );
    if (!rows.length) continue;
    const sources = rows
      .map((r) => resolveDayMediaSrc(r))
      .filter((s): s is DayMediaSrc => Boolean(s));
    result[key] = {
      dateKey: key,
      count: rows.length,
      thumbUrl: sources[0]?.url ?? null,
      thumbUrls: sources.map((s) => s.url),
      sources,
    };
  }

  if (config.mode !== 'production') return result;

  const supabase = getSupabase();
  if (!supabase) return result;

  const { data: dateFolders } = await supabase.storage.from(DAY_MEDIA_BUCKET).list(spaceId, {
    limit: 100,
  });
  const remoteDates = (dateFolders ?? [])
    .map((f) => f.name)
    .filter((name): name is string => Boolean(name) && unique.includes(name));

  await Promise.all(
    remoteDates.map(async (dateKey) => {
      const { data: files } = await supabase.storage
        .from(DAY_MEDIA_BUCKET)
        .list(`${spaceId}/${dateKey}`, { limit: 50 });
      const images = (files ?? []).filter((f) => f.name && !f.name.endsWith('/'));
      if (!images.length) return;
      const existing = result[dateKey];
      const remoteSources = images
        .map((file) => {
          const path = `${spaceId}/${dateKey}/${file.name}`;
          return resolveDayMediaSrc({
            id: file.name.replace(/\.[^.]+$/, ''),
            spaceId,
            dateKey,
            ownerId: '',
            mimeType: 'image/jpeg',
            fileName: file.name,
            storagePath: path,
            blob: null,
            createdAt: file.created_at ?? nowIso(),
          });
        })
        .filter((s): s is DayMediaSrc => Boolean(s));

      // Prefer local blob sources when present (same id); otherwise remote public URLs.
      const byId = new Map<string, DayMediaSrc>();
      for (const s of existing?.sources ?? []) byId.set(s.mediaId, s);
      for (const s of remoteSources) {
        const prev = byId.get(s.mediaId);
        if (prev?.kind === 'blob') continue;
        byId.set(s.mediaId, s);
      }
      const merged = [...byId.values()];
      const remoteCount = Math.max(existing?.count ?? 0, images.length);
      result[dateKey] = {
        dateKey,
        count: remoteCount,
        thumbUrl: merged[0]?.url ?? existing?.thumbUrl ?? null,
        thumbUrls: merged.map((s) => s.url),
        sources: merged,
      };
    }),
  );

  return result;
}

function publicObjectUrl(storagePath: string): string | null {
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data } = supabase.storage.from(DAY_MEDIA_BUCKET).getPublicUrl(storagePath);
  return data.publicUrl || null;
}

function transformObjectUrl(storagePath: string, width: number): string | null {
  if (!config.supabaseUrl) return null;
  const encoded = storagePath
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');
  return `${config.supabaseUrl}/storage/v1/render/image/public/${DAY_MEDIA_BUCKET}/${encoded}?width=${width}&resize=cover`;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(file);
  });
}
